import Card from '@/global/components/Card'
import { SwitchSelect } from '@/global/components/SwitchSelect'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
    isCondicaoAlcance,
    isCondicaoExecucao,
    isCondicaoId,
    type RotinaInput,
} from '@acme/core'
import IconButton from 'global/components/Button/IconButton'
import { PencilIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import {
    get,
    useFieldArray,
    useFormContext,
    useFormState,
    useWatch,
} from 'react-hook-form'
import { type Condicao, CondicaoDialog, IconeCondicao } from './CondicaoDialog'
import { ListaField } from './ListaField'
import { indiceOrdenado, motivoConectorTravado } from './ordem'
import { descreverCondicao, resumirParametrosCondicao } from './resumo'

/** Lista das condições ("Se"), cada evento com o próprio conector E/OU; cada uma se edita no {@link CondicaoDialog}. */
export function CondicoesField() {
    const { control } = useFormContext<RotinaInput>()
    const { fields, append, remove, move, update } = useFieldArray({
        control,
        name: 'condicoes',
    })
    const [editando, setEditando] = useState<number | 'nova' | null>(null)
    const condicoes = useWatch({ control, name: 'condicoes' }) ?? []
    const condicaoIds = condicoes.map(it => it.condicaoId)

    // Reposiciona a condição salva no grupo dela (alcance → execução → eventos).
    // Alcance e execução voltam para E: o "ou" só vale entre eventos.
    const salvarCondicao = (condicao: Condicao) => {
        const index = editando === 'nova' ? condicaoIds.length : editando
        if (index === null) return
        const isRestricao =
            isCondicaoAlcance(condicao.condicaoId) ||
            isCondicaoExecucao(condicao.condicaoId)
        const conector = isRestricao ? 'E' : (condicoes[index]?.conector ?? 'E')
        if (editando === 'nova') append({ ...condicao, conector })
        else update(index, { ...condicao, conector })
        const ids = [...condicaoIds]
        ids[index] = condicao.condicaoId
        move(index, indiceOrdenado(ids, index))
        setEditando(null)
    }

    return (
        <>
            <ListaField
                name="condicoes"
                rotuloAdicionar="Adicionar condição"
                onAdicionar={() => setEditando('nova')}
            >
                {fields.map((field, index) => (
                    <CondicaoItem
                        key={field.id}
                        index={index}
                        condicao={field}
                        motivoConectorTravado={motivoConectorTravado(
                            condicaoIds,
                            index,
                        )}
                        onEditar={() => setEditando(index)}
                        onRemover={() => remove(index)}
                    />
                ))}
            </ListaField>
            {editando !== null && (
                <CondicaoDialog
                    valor={
                        editando === 'nova'
                            ? undefined
                            : (fields[editando] as Condicao)
                    }
                    onClose={() => setEditando(null)}
                    onSalvar={salvarCondicao}
                />
            )}
        </>
    )
}

interface CondicaoItemProps {
    index: number
    condicao: RotinaInput['condicoes'][number]
    motivoConectorTravado: string | null
    onEditar: () => void
    onRemover: () => void
}

function CondicaoItem({
    index,
    condicao: { condicaoId, ocorre, parametros },
    motivoConectorTravado,
    onEditar,
    onRemover,
}: CondicaoItemProps) {
    const { errors } = useFormState({ name: `condicoes.${index}` })
    const erro = get(errors, `condicoes.${index}.condicaoId.message`)

    return (
        <Card
            className={cn(
                'flex flex-wrap items-center gap-3 p-3',
                erro && 'border border-error',
            )}
        >
            {index === 0 ? (
                <span className="w-24 text-sm uppercase text-muted-foreground">
                    Quando
                </span>
            ) : (
                <ConectorCondicao
                    index={index}
                    motivoTravado={motivoConectorTravado}
                />
            )}
            {isCondicaoId(condicaoId) && (
                <IconeCondicao condicaoId={condicaoId} />
            )}
            <div className="min-w-0 flex-1 basis-40">
                <p className="font-semibold">
                    {descreverCondicao(condicaoId, ocorre)}
                </p>
                <p className="truncate text-sm text-muted-foreground">
                    {isCondicaoId(condicaoId)
                        ? resumirParametrosCondicao(condicaoId, parametros)
                        : ''}
                </p>
                {erro && <p className="text-xs text-error">{erro}</p>}
            </div>
            <IconButton title="Editar condição" onClick={onEditar}>
                <PencilIcon />
            </IconButton>
            <IconButton
                title="Remover condição"
                color="error"
                onClick={onRemover}
            >
                <Trash2Icon />
            </IconButton>
        </Card>
    )
}

const OPCOES_CONECTOR = [
    { value: 'E', label: 'E' },
    { value: 'OU', label: 'OU' },
] as const

/**
 * Liga a condição à anterior, como a "Lógica" do painel de filtros da tabela.
 * Travada em E, mostra o `motivoTravado` ao passar o mouse.
 */
function ConectorCondicao({
    index,
    motivoTravado,
}: {
    index: number
    motivoTravado: string | null
}) {
    const { setValue } = useFormContext<RotinaInput>()
    const conector = useWatch<RotinaInput, `condicoes.${number}.conector`>({
        name: `condicoes.${index}.conector`,
    })

    // Botão desabilitado não recebe o mouse; o span segura o tooltip.
    return (
        <Tooltip title={motivoTravado}>
            <span className="w-24">
                <SwitchSelect
                    label="Lógica"
                    variant="outlined"
                    size="small"
                    className="w-full"
                    options={OPCOES_CONECTOR}
                    value={motivoTravado ? 'E' : (conector ?? 'E')}
                    disabled={!!motivoTravado}
                    onChange={valor =>
                        setValue(`condicoes.${index}.conector`, valor, {
                            shouldDirty: true,
                        })
                    }
                />
            </span>
        </Tooltip>
    )
}
